// The inbox's third column: who the customer is and what the team knows,
// next to the conversation. Assignment, tags and consent live here. Each
// control is shown only to a role the server lets use it.
import { Link } from 'react-router'
import { ExternalLink } from 'lucide-react'
import { useAssign, useTeammates, type Conversation } from '../api/inbox'
import { useContact } from '../api/contacts'
import { RequestFailed } from '../api/client'
import { Consent, ContactTagEditor } from './ContactFacts'
import { Initials, WindowBadge } from '../pages/InboxPage'
import { fullTime, useNow, windowState } from '../lib/format'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

type Role = 'owner' | 'admin' | 'agent' | 'viewer'

// Radix Select reserves the empty value, so "nobody" has its own.
const nobody = 'unassigned'

export function CustomerPanel({ conv, role, meId }: { conv: Conversation; role: Role; meId: string }) {
  const contact = useContact(conv.contact.id)
  const now = useNow()
  const w = windowState(conv.serviceWindowExpiresAt, now)
  const admin = role === 'owner' || role === 'admin'

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Initials name={conv.contact.name} className="size-11 text-sm" />
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold">{conv.contact.name}</h3>
          <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
            {conv.contact.waId ? `+${conv.contact.waId}` : 'Number not shared'}
          </p>
        </div>
      </div>

      <Fact label="Reply time">
        <WindowBadge w={w} />
      </Fact>
      <Fact label="Writes to">
        <span className="text-sm">+{conv.displayNumber}{conv.entryPoint === 'referral' && ', came from an ad'}</span>
      </Fact>
      <Fact label="Assigned to">
        <AssignControl conv={conv} role={role} meId={meId} />
      </Fact>

      {contact.isPending ? (
        <div role="status" aria-label="Loading the customer's details" className="flex flex-col gap-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : contact.isError ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(contact.error as RequestFailed).message}</p>
      ) : (
        <>
          <Fact label="Tags">
            <ContactTagEditor c={contact.data} canTag={role !== 'viewer'} />
          </Fact>
          <Consent c={contact.data} admin={admin} />
          {contact.data.profileName && contact.data.profileName !== conv.contact.name && (
            <p className="text-xs text-muted-foreground">On WhatsApp as {contact.data.profileName}.</p>
          )}
          <p className="text-xs text-muted-foreground">Added {fullTime(contact.data.createdAt)}.</p>
        </>
      )}

      <Button asChild variant="outline" size="sm" className="w-fit">
        <Link to={`/contacts/${conv.contact.id}`}>
          <ExternalLink className="size-4" aria-hidden="true" />
          Open in Contacts
        </Link>
      </Button>
    </div>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

// Owners and admins pick anyone who can reply; an agent can take an
// unassigned conversation or release their own; a viewer reads.
function AssignControl({ conv, role, meId }: { conv: Conversation; role: Role; meId: string }) {
  const assign = useAssign(conv.id)
  const team = useTeammates()
  if (role === 'viewer') {
    return <span className="text-sm">{conv.assignee ? conv.assignee.name : 'Nobody yet'}</span>
  }
  const error = assign.error as RequestFailed | null
  const mine = conv.assignee?.id === meId
  const assignable = (team.data ?? []).filter((t) => t.role !== 'viewer')
  return (
    <div className="flex flex-col gap-2">
      {role === 'agent' ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">{conv.assignee ? (mine ? 'You' : conv.assignee.name) : 'Nobody yet'}</span>
          {!conv.assignee && (
            <Button size="sm" disabled={assign.isPending} onClick={() => assign.mutate(meId)}>Assign to me</Button>
          )}
          {mine && (
            <Button size="sm" variant="outline" disabled={assign.isPending} onClick={() => assign.mutate(undefined)}>Release</Button>
          )}
        </div>
      ) : (
        <Select
          value={conv.assignee?.id ?? nobody}
          disabled={assign.isPending || team.isPending}
          onValueChange={(v) => assign.mutate(v === nobody ? undefined : v)}
        >
          <SelectTrigger aria-label="Assigned to" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={nobody}>Unassigned</SelectItem>
            {assignable.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
    </div>
  )
}
